import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {handlerError} from "../../../store/handleError";
import {AppDispatch, RootState} from "../../../store";
import {dispatchAPI} from "../api";
import {IDispatchRoute, TrustState} from "../../../models/IDispatch";
import {setMessage} from "../../messages/model/slice";
import {MESSAGE_SEVERITY} from "../../../utils/const";

type ThunkConfig = {dispatch: AppDispatch; state: RootState; rejectValue: string};

/**
 * ============================================================================
 * Кабинет водителя (ТЗ 5.2): отдельный срез состояния. Slice и thunks в одном
 * файле — зона маленькая (один рейс + история), разносить незачем.
 * ============================================================================
 */

interface IDriverDayState {
    /** День кабинета (YYYY-MM-DD), по умолчанию — сегодня. */
    date: string;
    /** null = «задания ещё не утверждены» (или рейса нет вовсе). */
    route: IDispatchRoute | null;
    /** Состояние доверия координат машины рейса — подсветка «Зона помех». */
    trustState: TrustState;
    history: IDispatchRoute[];
    isLoading: boolean;
    isHistoryLoading: boolean;
    /** Точка, по которой сейчас идёт действие (кнопки-лоадеры). */
    actingJobId: string | null;
}

const initialState: IDriverDayState = {
    date: dayjs().format("YYYY-MM-DD"),
    route: null,
    trustState: "no_data",
    history: [],
    isLoading: false,
    isHistoryLoading: false,
    actingJobId: null,
};

const reportError = (e: unknown, dispatch: AppDispatch, fallback: string) => {
    const text = e instanceof Error && e.message ? e.message : fallback;
    dispatch(setMessage({severity: MESSAGE_SEVERITY.error, text}));
};

// ── Thunks ──────────────────────────────────────────────────────────────────

/** Мой рейс + trust-state одним заходом (и первичная загрузка, и поллинг). */
export const fetchDriverDay = createAsyncThunk<{route: IDispatchRoute | null; trustState: TrustState}, string | undefined, ThunkConfig>(
    "driverDay/fetch",
    async (dateArg, {getState, rejectWithValue}) => {
        try {
            const date = dateArg ?? getState().driverDay.date;
            const [route, trust] = await Promise.all([
                dispatchAPI.getMyRoute(date),
                dispatchAPI.getMyTrustState(date),
            ]);
            return {route, trustState: (trust.trust_state as TrustState) ?? "no_data"};
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchDriverHistory = createAsyncThunk<IDispatchRoute[], void, ThunkConfig>(
    "driverDay/history",
    async (_, {rejectWithValue}) => {
        try {
            return await dispatchAPI.getMyRoutes(30, 0);
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

/** Общая обёртка «действие над своей точкой → перечитать день». */
const driverJobAction = (
    type: string,
    call: (id: string, extra?: string) => Promise<unknown>,
    fallbackError: string,
) =>
    createAsyncThunk<string, {id: string; extra?: string}, ThunkConfig>(
        type,
        async ({id, extra}, {dispatch, rejectWithValue}) => {
            try {
                await call(id, extra);
                void dispatch(fetchDriverDay(undefined));
                return id;
            } catch (e) {
                reportError(e, dispatch, fallbackError);
                return rejectWithValue(handlerError(e));
            }
        },
    );

export const fetchDriverArrive = driverJobAction("driverDay/arrive", (id) => dispatchAPI.arriveJob(id), "Не удалось отметить прибытие.");
export const fetchDriverClose = driverJobAction("driverDay/close", (id, comment) => dispatchAPI.closeJob(id, comment), "Не удалось закрыть точку.");
export const fetchDriverFail = driverJobAction("driverDay/fail", (id, reason) => dispatchAPI.failJob(id, reason ?? ""), "Не удалось отметить неуспех.");

export const fetchDriverReorder = createAsyncThunk<IDispatchRoute, string[], ThunkConfig>(
    "driverDay/reorder",
    async (jobIds, {getState, dispatch, rejectWithValue}) => {
        try {
            const date = getState().driverDay.date;
            return await dispatchAPI.reorderMyRoute(date, jobIds);
        } catch (e) {
            reportError(e, dispatch, "Не удалось сохранить порядок.");
            return rejectWithValue(handlerError(e));
        }
    },
);

// ── Slice ───────────────────────────────────────────────────────────────────

export const DriverDaySlice = createSlice({
    name: "driverDay",
    initialState,
    reducers: {
        setDriverDate: (state, action: PayloadAction<string>) => {
            state.date = action.payload;
            state.route = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDriverDay.pending, (state) => {
                // Тихий поллинг: лоадер только при пустом состоянии.
                if (!state.route) state.isLoading = true;
            })
            .addCase(fetchDriverDay.fulfilled, (state, action) => {
                state.route = action.payload.route;
                state.trustState = action.payload.trustState;
                state.isLoading = false;
                state.actingJobId = null;
            })
            .addCase(fetchDriverDay.rejected, (state) => {
                state.isLoading = false;
                state.actingJobId = null;
            })
            .addCase(fetchDriverHistory.pending, (state) => {
                state.isHistoryLoading = true;
            })
            .addCase(fetchDriverHistory.fulfilled, (state, action) => {
                state.history = action.payload;
                state.isHistoryLoading = false;
            })
            .addCase(fetchDriverHistory.rejected, (state) => {
                state.isHistoryLoading = false;
            })
            .addCase(fetchDriverReorder.fulfilled, (state, action) => {
                state.route = action.payload;
            });

        // Кнопочные лоадеры действий над точкой.
        for (const thunk of [fetchDriverArrive, fetchDriverClose, fetchDriverFail]) {
            builder
                .addCase(thunk.pending, (state, action) => {
                    state.actingJobId = action.meta.arg.id;
                })
                .addCase(thunk.rejected, (state) => {
                    state.actingJobId = null;
                });
        }
    },
});

export const {setDriverDate} = DriverDaySlice.actions;
export default DriverDaySlice.reducer;
